import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter } from '../sectepe-ui/index';
import type { Recht } from '../berechtigungen';
import type { User } from '../services/auth.service';
import { RECHT_GRUPPEN, RECHTE_JE_ROLLE, angezeigteAppRolle, appRolleLabel, istPersonRecht } from './rollen';

/** Zustand eines Rechts je Person: wie die Rolle, zusaetzlich erteilt oder entzogen. */
type Zustand = 'rolle' | 'erteilt' | 'entzogen';

export interface RechteAenderung {
  zusatz: Recht[];
  entzug: Recht[];
}

interface RechteDialogProps {
  person: User | null;
  /** Speichert beide Listen; der Aufrufer meldet Erfolg und Fehler. */
  onSpeichern: (person: User, aenderung: RechteAenderung) => Promise<void>;
  onSchliessen: () => void;
}

/**
 * "Rechte anpassen" (Rechte je Person, Entscheidung 17): einzelne Rechte
 * zusaetzlich zur Rolle erteilen oder der Rolle entziehen. Gilt auch im
 * Keycloak-Betrieb, die Anmeldung schreibt die Listen nicht zurueck.
 *
 * Die Grundlage je Recht ist die Rollen-Matrix (RECHTE_JE_ROLLE, Kopie aus
 * dem Backend); erteilen laesst sich nur, was die Rolle nicht hat, entziehen
 * nur, was sie hat. Verwaltungsrechte (admin.*) bleiben an der Rolle. Das
 * Backend prueft und bereinigt die Listen ohnehin (rechteVon, Route-Validator).
 */
export const RechteDialog: React.FC<RechteDialogProps> = ({ person, onSpeichern, onSchliessen }) => {
  return (
    <Modal open={Boolean(person)} onOpenChange={(o) => { if (!o) onSchliessen(); }}>
      {person && <RechteFormular key={person.id} person={person} onSpeichern={onSpeichern} onSchliessen={onSchliessen} />}
    </Modal>
  );
};

const RechteFormular: React.FC<{ person: User } & Omit<RechteDialogProps, 'person'>> = ({ person, onSpeichern, onSchliessen }) => {
  const rolle = angezeigteAppRolle(person);
  const ausRolle = RECHTE_JE_ROLLE[rolle];
  const [zustaende, setZustaende] = useState<Record<string, Zustand>>(() => {
    const start: Record<string, Zustand> = {};
    for (const r of person.rechteZusatz ?? []) start[r] = 'erteilt';
    for (const r of person.rechteEntzug ?? []) start[r] = 'entzogen';
    return start;
  });
  const [speichert, setSpeichert] = useState(false);

  const zustandVon = (recht: Recht): Zustand => {
    const z = zustaende[recht] ?? 'rolle';
    // Ein gespeicherter Zusatz, den die Rolle inzwischen selbst hat (oder ein Entzug,
    // den sie nicht hat), wirkt nicht und wird beim Speichern verworfen.
    if (z === 'erteilt' && ausRolle.includes(recht)) return 'rolle';
    if (z === 'entzogen' && !ausRolle.includes(recht)) return 'rolle';
    return z;
  };
  const wirksam = (recht: Recht): boolean => {
    const z = zustandVon(recht);
    return z === 'erteilt' || (z === 'rolle' && ausRolle.includes(recht));
  };

  const alleRechte = RECHT_GRUPPEN.flatMap((g) => g.rechte.map((r) => r.recht));
  const aenderung: RechteAenderung = {
    zusatz: alleRechte.filter((r) => zustandVon(r) === 'erteilt'),
    entzug: alleRechte.filter((r) => zustandVon(r) === 'entzogen'),
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    setSpeichert(true);
    try {
      await onSpeichern(person, aenderung);
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <ModalContent>
      <form onSubmit={speichern} aria-label="Rechte anpassen">
        <ModalHeader>
          <ModalTitle>Rechte von {person.name}</ModalTitle>
          <ModalDescription>
            Rolle {appRolleLabel(person).label}. Einzelne Rechte zusätzlich erteilen oder der Rolle entziehen; gilt ab der nächsten Anfrage der Person.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="rechte-dialog">
            {RECHT_GRUPPEN.map((gruppe) => (
              <fieldset key={gruppe.label} className="rechte-gruppe">
                <legend>{gruppe.label}</legend>
                {gruppe.rechte.map(({ recht, label }) => {
                  const hatRolle = ausRolle.includes(recht);
                  const z = zustandVon(recht);
                  const aenderbar = hatRolle || istPersonRecht(recht);
                  return (
                    <div key={recht} className="rechte-zeile">
                      <span className="rechte-zeile__label">
                        {label}
                        <code className="text-xs text-secondary"> {recht}</code>
                      </span>
                      <select
                        aria-label={`${gruppe.label}: ${label}`}
                        value={z}
                        disabled={!aenderbar}
                        onChange={(ev) => setZustaende((alt) => ({ ...alt, [recht]: ev.target.value as Zustand }))}
                      >
                        <option value="rolle">{hatRolle ? 'Aus Rolle: ja' : 'Aus Rolle: nein'}</option>
                        {!hatRolle && istPersonRecht(recht) && <option value="erteilt">Zusätzlich erteilen</option>}
                        {hatRolle && <option value="entzogen">Entziehen</option>}
                      </select>
                      <span className={`rechte-zeile__wirkung${z !== 'rolle' ? ' rechte-zeile__wirkung--angepasst' : ''}`}>
                        {wirksam(recht) ? 'darf' : 'darf nicht'}
                      </span>
                    </div>
                  );
                })}
              </fieldset>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          {(aenderung.zusatz.length > 0 || aenderung.entzug.length > 0) && (
            <button type="button" className="btn btn-ghost" onClick={() => setZustaende({})}>Auf Rolle zurücksetzen</button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onSchliessen}>Abbrechen</button>
          <button type="submit" className="btn btn-primary" disabled={speichert}>{speichert ? 'Speichert…' : 'Speichern'}</button>
        </ModalFooter>
      </form>
    </ModalContent>
  );
};

/** Hat die Person Rechte je Person (Zusatz oder Entzug)? Fuer das Badge in der Liste. */
export const hatAngepassteRechte = (u: Pick<User, 'rechteZusatz' | 'rechteEntzug'>): boolean =>
  (u.rechteZusatz?.length ?? 0) > 0 || (u.rechteEntzug?.length ?? 0) > 0;

export default RechteDialog;
